import { GridColDef, GridValidRowModel } from "@mui/x-data-grid";
import {
  FieldConfig,
  FieldType,
  FilterCondition,
  OptionalFieldConfig,
} from "src/models/filter"; // Assuming this path is correct

const NO_CONFIG_ERROR =
  "Table controls configuration has not been provided or is empty.";

export type TableControlConfigItem = GridColDef & {
  fieldType?: FieldType;
} & OptionalFieldConfig;

export type TableControlsConfig = TableControlConfigItem[];

export class TableControls<R extends GridValidRowModel = any> {
  private readonly tableControlsConfig: TableControlsConfig | undefined;

  constructor(tableControlsConfig?: TableControlsConfig) {
    if (!tableControlsConfig || tableControlsConfig.length === 0) {
      throw new Error(NO_CONFIG_ERROR);
    } else {
      this.tableControlsConfig = tableControlsConfig;
    }
  }

  /**
   * Extracts and returns only the GridColDef properties from the configuration,
   * suitable for passing to the MUI DataGrid columns prop.
   */
  public getTableColumns(): GridColDef<R>[] {
    if (!this.tableControlsConfig) {
      throw new Error(NO_CONFIG_ERROR);
    }

    return this.tableControlsConfig.map((configItem) => {
      const { fieldType, helperText, options, ...gridColProps } = configItem;

      return gridColProps;
    });
  }

  /**
   * Extracts and returns the configuration needed for a separate filter component.
   */
  public getFilterConfig(): FieldConfig[] {
    if (!this.tableControlsConfig) {
      throw new Error(NO_CONFIG_ERROR);
    }

    return this.tableControlsConfig
      .filter((c) => c.filterable)
      .map((c) => {
        if (!c.field) {
          throw new Error(
            `Configuration item is missing the required 'field' property for filter config: ${JSON.stringify(
              c
            )}`
          );
        }

        if (!c.fieldType) {
          throw new Error(
            `Configuration item marked as filterable but no type provided: ${JSON.stringify(
              c
            )}`
          );
        }

        return {
          id: c.field,
          type: c.fieldType,
          label: typeof c.headerName === "string" ? c.headerName : c.field,
          helperText: c.helperText,
          options: c.options,
        };
      });
  }

  public buildFilterConditions(
    filters: Record<string, string>
  ): FilterCondition[] {
    if (!filters || Object.keys(filters).length === 0) {
      return [];
    }

    const filterCfg = this.getFilterConfig();

    return filterCfg
      .filter((cfg) => !!filters[cfg.id])
      .map((cfg) => ({
        field: cfg.id,
        operator: "==",
        value: filters[cfg.id],
      }));
  }
}
